import { CameraOutlined, DeleteOutlined, EllipsisOutlined, EyeOutlined, PictureOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Dropdown,
  Empty,
  Flex,
  Image,
  type MenuProps,
  Modal,
  Space,
  Spin,
  Typography,
} from 'antd';
import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import type { InventorizationTechnicalDetails } from '@/features/inventorization/domain/inventorization-technical';
import { photoWizardText } from '@/features/inventorization/photos/photo-wizard-i18n';
import {
  canContinueStep,
  createCaptureSlot,
  createWizardSteps,
  findFirstIncompleteStepIndex,
  getSlotPhotos,
  getStepDescription,
  getStepTitle,
  getWizardPhotoIds,
  hasCompletedInventorizePhotoWizard,
  hasCompletedLlmExtraction,
  hasCompletedRequiredWizardSteps,
  PHOTO_WIZARD_STEP_KINDS,
  type WizardStep,
} from '@/features/inventorization/photos/photo-wizard-steps';
import { usePhotoUrls } from '@/features/inventorization/photos/usePhotoUrls';
import { PhotoWizardExtractionStep } from '@/routers/inventorization/components/photos/PhotoWizardExtractionStep';
import {
  ContextAccessibilityPicker,
  MeterMountTypePicker,
} from '@/routers/inventorization/components/photos/PhotoWizardStepPickers';
import { createCapturedPhotoAssetFromFile } from '@/shared/camera/browser-photo-capture';
import type { CaptureSession, LocalPhotoRef } from '@/shared/capture/capture-session';
import type { PhotoSlotDefinition } from '@/shared/capture/inventorization-photo-slots';

export { hasCompletedInventorizePhotoWizard };

interface InventorizePhotoWizardProps {
  error: string | null;
  isCapturingSlotId: string | null;
  isSubmittingLlm: boolean;
  onBack: () => void;
  onCapture: (
    slot: PhotoSlotDefinition,
    asset: Awaited<ReturnType<typeof createCapturedPhotoAssetFromFile>>,
    replacedPhoto?: LocalPhotoRef,
  ) => Promise<unknown>;
  onContinueWithoutLlm: (photoIds: string[]) => Promise<unknown>;
  onDelete: (photo: LocalPhotoRef) => Promise<unknown>;
  onRefresh: () => Promise<unknown> | unknown;
  onSaveTechnical: (patch: Partial<InventorizationTechnicalDetails>) => Promise<unknown> | unknown;
  onSubmitLlm: (photoIds: string[]) => Promise<unknown>;
  session: CaptureSession;
  technical: InventorizationTechnicalDetails;
}

export function InventorizePhotoWizard({
  error,
  isCapturingSlotId,
  isSubmittingLlm,
  onBack,
  onCapture,
  onContinueWithoutLlm,
  onDelete,
  onRefresh,
  onSaveTechnical,
  onSubmitLlm,
  session,
  technical,
}: InventorizePhotoWizardProps) {
  const wizardSteps = useMemo(() => createWizardSteps(technical), [technical]);
  const firstIncompleteStepIndex = findFirstIncompleteStepIndex(session, technical);
  const [slotIndex, setSlotIndex] = useState(
    firstIncompleteStepIndex === -1 ? 0 : Math.min(firstIncompleteStepIndex, wizardSteps.length - 1),
  );
  const [selectedPhoto, setSelectedPhoto] = useState<LocalPhotoRef | null>(null);
  const [isExtractionStepOpen, setIsExtractionStepOpen] = useState(false);
  const currentStep = wizardSteps[slotIndex] ?? wizardSteps[0] ?? null;
  const currentSlot = currentStep?.kind === PHOTO_WIZARD_STEP_KINDS.photo ? currentStep.slot : null;
  const currentSlotPhotos = currentSlot ? getSlotPhotos(session, currentSlot) : [];
  const submittedPhotoIds = useMemo(() => getWizardPhotoIds(session, technical), [session, technical]);
  const allRequiredPhotosCaptured = hasCompletedRequiredWizardSteps(session, technical);
  const hasCompletedLlm = hasCompletedLlmExtraction(session);
  const photoUrls = usePhotoUrls(selectedPhoto ? [selectedPhoto] : currentSlotPhotos);
  const selectedPhotoUrl = selectedPhoto ? photoUrls[selectedPhoto.id]?.originalUrl : null;
  const isLastStep = slotIndex >= wizardSteps.length - 1;

  useEffect(() => {
    if (slotIndex >= wizardSteps.length) {
      setSlotIndex(Math.max(wizardSteps.length - 1, 0));
    }
  }, [slotIndex, wizardSteps.length]);

  async function handleFile(file: File | undefined, replacedPhoto?: LocalPhotoRef) {
    if (!(file && currentSlot)) {
      return;
    }

    const asset = await createCapturedPhotoAssetFromFile(file);
    await onCapture(
      replacedPhoto ? currentSlot : createCaptureSlot(currentSlot, currentSlotPhotos),
      asset,
      replacedPhoto,
    );
    await onRefresh();
  }

  if (allRequiredPhotosCaptured && !hasCompletedLlm && isExtractionStepOpen) {
    return (
      <PhotoWizardExtractionStep
        error={error}
        isSubmittingLlm={isSubmittingLlm}
        onBack={() => setIsExtractionStepOpen(false)}
        onContinueWithoutLlm={onContinueWithoutLlm}
        onRefresh={onRefresh}
        onSubmitLlm={onSubmitLlm}
        session={session}
        submittedPhotoIds={submittedPhotoIds}
      />
    );
  }

  if (!currentStep) {
    return (
      <Empty
        description={photoWizardText('empty.noSteps', 'No photo steps are configured.')}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  const canContinueCurrentStep = canContinueStep(currentStep, session, technical);

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Typography.Text type="secondary">
          {photoWizardText('stepCounter', 'Photos {current} / {total}', {
            current: slotIndex + 1,
            total: wizardSteps.length,
          })}
        </Typography.Text>
        <Typography.Title level={2} style={{ margin: 0 }}>
          {getStepTitle(currentStep)}
        </Typography.Title>
        <Typography.Paragraph type="secondary">{getStepDescription(currentStep)}</Typography.Paragraph>
      </div>

      {error ? <Alert closable message={error} showIcon type="error" /> : null}

      <PhotoWizardCurrentStep
        currentSlotPhotos={currentSlotPhotos}
        isCapturingSlotId={isCapturingSlotId}
        onDelete={photo => void onDelete(photo).then(onRefresh)}
        onFile={(file, replacedPhoto) => void handleFile(file, replacedPhoto)}
        onSaveTechnical={onSaveTechnical}
        onSelectPhoto={setSelectedPhoto}
        photoUrls={photoUrls}
        step={currentStep}
        technical={technical}
      />

      <Flex gap="small" justify="space-between" wrap>
        <Button
          onClick={() => {
            if (slotIndex > 0) {
              setSlotIndex(slotIndex - 1);
            } else {
              onBack();
            }
          }}
          size="large"
        >
          {photoWizardText('actions.back', 'Back')}
        </Button>
        {isLastStep ? (
          <Button
            disabled={!allRequiredPhotosCaptured || isSubmittingLlm}
            onClick={() => {
              setIsExtractionStepOpen(true);
              void onSubmitLlm(submittedPhotoIds).then(result => {
                if (result) {
                  void onRefresh();
                }
              });
            }}
            size="large"
            type="primary"
          >
            {photoWizardText('actions.submitPhotos', 'Submit photos')}
          </Button>
        ) : (
          <Button
            disabled={!canContinueCurrentStep}
            onClick={() => setSlotIndex(slotIndex + 1)}
            size="large"
            type="primary"
          >
            {photoWizardText('actions.next', 'Next')}
          </Button>
        )}
      </Flex>

      <PhotoPreviewModal
        onClose={() => setSelectedPhoto(null)}
        open={Boolean(selectedPhoto)}
        photoUrl={selectedPhotoUrl ?? null}
      />
    </Space>
  );
}

function PhotoWizardCurrentStep({
  currentSlotPhotos,
  isCapturingSlotId,
  onDelete,
  onFile,
  onSaveTechnical,
  onSelectPhoto,
  photoUrls,
  step,
  technical,
}: {
  currentSlotPhotos: LocalPhotoRef[];
  isCapturingSlotId: string | null;
  onDelete: (photo: LocalPhotoRef) => void;
  onFile: (file: File | undefined, replacedPhoto?: LocalPhotoRef) => void;
  onSaveTechnical: (patch: Partial<InventorizationTechnicalDetails>) => Promise<unknown> | unknown;
  onSelectPhoto: (photo: LocalPhotoRef) => void;
  photoUrls: Record<string, { originalUrl: string | null; thumbnailUrl: string | null }>;
  step: WizardStep;
  technical: InventorizationTechnicalDetails;
}) {
  if (step.kind === PHOTO_WIZARD_STEP_KINDS.photo) {
    return (
      <PhotoWizardPhotoGrid
        isCapturingSlotId={isCapturingSlotId}
        onDelete={onDelete}
        onFile={onFile}
        onSelectPhoto={onSelectPhoto}
        photos={currentSlotPhotos}
        photoUrls={photoUrls}
        slot={step.slot}
      />
    );
  }

  if (step.kind === PHOTO_WIZARD_STEP_KINDS.accessibility) {
    return <ContextAccessibilityPicker onSaveTechnical={onSaveTechnical} technical={technical} />;
  }

  return <MeterMountTypePicker onSaveTechnical={onSaveTechnical} technical={technical} />;
}

const VISIBLE_PHOTO_SLOTS = 3;

export function PhotoWizardPhotoGrid({
  isCapturingSlotId,
  onDelete,
  onFile,
  onSelectPhoto,
  photos,
  photoUrls,
  slot,
}: {
  isCapturingSlotId: string | null;
  onDelete: (photo: LocalPhotoRef) => void;
  onFile: (file: File | undefined, replacedPhoto?: LocalPhotoRef) => void;
  onSelectPhoto: (photo: LocalPhotoRef) => void;
  photos: LocalPhotoRef[];
  photoUrls: Record<string, { thumbnailUrl: string | null }>;
  slot: PhotoSlotDefinition;
}) {
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const galleryInputRef = useRef<HTMLInputElement | null>(null);
  const photoToReplaceRef = useRef<LocalPhotoRef | undefined>(undefined);
  const visibleTileCount = Math.max(VISIBLE_PHOTO_SLOTS, photos.length + 1);

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    onFile(file, photoToReplaceRef.current);
    photoToReplaceRef.current = undefined;
  }

  return (
    <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
      {Array.from({ length: visibleTileCount }, (_, index) => {
        const photo = photos[index];
        const thumbnailUrl = photo ? photoUrls[photo.id]?.thumbnailUrl : null;

        return (
          <PhotoTile
            disabled={Boolean(isCapturingSlotId)}
            index={index}
            isFilled={Boolean(photo)}
            isLoading={!photo && Boolean(isCapturingSlotId)}
            key={photo?.id ?? `${slot.id}-${index}`}
            menuItems={[
              photo
                ? {
                    key: 'preview',
                    icon: <EyeOutlined />,
                    label: photoWizardText('actions.preview', 'Preview'),
                    onClick: () => onSelectPhoto(photo),
                  }
                : null,
              {
                key: 'camera',
                icon: <CameraOutlined />,
                label: photo
                  ? photoWizardText('actions.retake', 'Retake')
                  : photoWizardText('actions.takePhoto', 'Take photo'),
                onClick: () => {
                  photoToReplaceRef.current = photo;
                  cameraInputRef.current?.click();
                },
              },
              {
                key: 'choose',
                icon: <PictureOutlined />,
                label: photoWizardText('actions.chooseFromGallery', 'Choose from gallery'),
                onClick: () => {
                  photoToReplaceRef.current = photo;
                  galleryInputRef.current?.click();
                },
              },
              photo
                ? {
                    key: 'delete',
                    danger: true,
                    icon: <DeleteOutlined />,
                    label: photoWizardText('actions.delete', 'Delete'),
                    onClick: () => onDelete(photo),
                  }
                : null,
            ]}
            thumbnailUrl={thumbnailUrl ?? null}
          />
        );
      })}
      <input
        accept="image/*"
        capture="environment"
        hidden
        onChange={handleFileChange}
        ref={cameraInputRef}
        type="file"
      />
      <input accept="image/*" hidden onChange={handleFileChange} ref={galleryInputRef} type="file" />
    </div>
  );
}

function PhotoTile({
  disabled,
  index,
  isFilled,
  isLoading,
  menuItems,
  thumbnailUrl,
}: {
  disabled: boolean;
  index: number;
  isFilled: boolean;
  isLoading: boolean;
  menuItems: MenuProps['items'];
  thumbnailUrl: string | null;
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <Dropdown
      autoFocus
      disabled={disabled}
      menu={{ items: menuItems, onClick: () => setIsMenuOpen(false) }}
      onOpenChange={setIsMenuOpen}
      open={isMenuOpen}
      trigger={['click', 'contextMenu']}
    >
      <Card
        aria-busy={isLoading}
        aria-disabled={disabled}
        aria-expanded={isMenuOpen}
        aria-haspopup="menu"
        aria-label={
          isFilled
            ? photoWizardText('tile.photoActions', 'Actions for photo {number}', { number: index + 1 })
            : photoWizardText('tile.addPhoto', 'Add photo {number}', { number: index + 1 })
        }
        hoverable
        onKeyDown={event => {
          if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            setIsMenuOpen(!isMenuOpen);
          }
        }}
        role="button"
        style={{
          aspectRatio: '1 / 1',
          borderStyle: isFilled ? 'solid' : 'dashed',
          cursor: 'pointer',
          flex: 1,
          minWidth: 0,
        }}
        styles={{ body: { height: '100%', padding: 8 } }}
        tabIndex={0}
      >
        <Flex align="center" justify="center" style={{ height: '100%', overflow: 'hidden' }}>
          {thumbnailUrl ? (
            <>
              <img
                alt={photoWizardText('tile.capturedAlt', 'Captured {number}', { number: index + 1 })}
                src={thumbnailUrl}
                style={{ height: '100%', objectFit: 'cover', width: '100%' }}
              />
              <EllipsisOutlined
                aria-hidden
                style={{
                  background: 'rgba(0, 0, 0, 0.6)',
                  borderRadius: 4,
                  color: 'white',
                  fontSize: 24,
                  padding: 4,
                  position: 'absolute',
                  right: 12,
                  top: 12,
                }}
              />
            </>
          ) : (
            <Space align="center" direction="vertical" size={4}>
              {isLoading ? <Spin /> : <CameraOutlined style={{ fontSize: 22 }} />}
              <Typography.Title level={2} style={{ margin: 0 }}>
                {index + 1}
              </Typography.Title>
              <Typography.Text type="secondary">{photoWizardText('tile.tapToAdd', 'Tap to add')}</Typography.Text>
            </Space>
          )}
        </Flex>
      </Card>
    </Dropdown>
  );
}

export function PhotoPreviewModal({
  onClose,
  open,
  photoUrl,
  title,
}: {
  onClose: () => void;
  open: boolean;
  photoUrl: string | null;
  title?: string;
}) {
  return (
    <Modal
      footer={
        <Button key="close" onClick={onClose} type="primary">
          {photoWizardText('actions.close', 'Close')}
        </Button>
      }
      onCancel={onClose}
      open={open}
      style={{ maxWidth: '100vw', top: 0 }}
      styles={{ body: { padding: 0 } }}
      title={title ?? photoWizardText('preview.title', 'Photo')}
      width="100vw"
    >
      {photoUrl ? (
        <Image
          alt={photoWizardText('preview.capturedAlt', 'Captured meter evidence')}
          preview={false}
          src={photoUrl}
          width="100%"
        />
      ) : (
        <Empty
          description={photoWizardText('preview.loading', 'Photo preview is loading.')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}
    </Modal>
  );
}
